import React, { Component } from "react";
import { connect } from "react-redux";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import Select from "react-select";
import { projSelectorConstants } from "../../_constants/projSelectorConstants";
import distOptions from "../../_constants/distOptions";
import memoizeOne from "memoize-one";
import unitActions from "../../_actions/unitActions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

/**
 * Allows user to change what projects are shown in ProjSelector dropdown.
 * Since changes should be cancellable, derive "showProjectsFor" from props
 * into the state to keep changes internal to the component.
 */
class ProjListSelectorModal extends Component {
  state = {
    showProjectsFor: {}, // Derived from props.
    selectedDistrict: null,
    selectedUnit: null,
    isChanged: false,
  };

  componentDidMount() {
    const { dispatch, tabId, showProjectsFor } = this.props;
    dispatch(unitActions.getAll(null, tabId));
    this.setState({ showProjectsFor: { ...showProjectsFor } });
  }

  handleChange_radio = (e) =>
    this.setState({
      showProjectsFor: {
        type: e.target.name,
        lookupId:
          e.target.name === projSelectorConstants.USER_ASSIGNED
            ? this.props.user.userId
            : null,
      },
      isChanged: true,
    });

  handleChange_select = (type, fieldOptionName) => (selectedOption) => {
    this.setState({
      showProjectsFor: { type, lookupId: selectedOption.value },
      [fieldOptionName]: selectedOption,
      isChanged: true,
    });
  };

  handleSubmit = (e) => {
    e.preventDefault();
    const { showProjectsFor, isChanged } = this.state;
    const { resetProjectId, updateFilter, toggleProjListSelectorModal } = this.props;

    // If project list filters are changed, re-fetch list
    if (isChanged) {
      updateFilter(showProjectsFor);

      // resetProjectId() is a callback provided by the component hosting ProjSelector,
      // and is used to clear the selected project (if one exists) if the filters are
      // changed and the list is refreshed.
      if (resetProjectId) resetProjectId();
    }
    toggleProjListSelectorModal();
  };

  formIsIncomplete = () => {
    const { selectedDistrict, selectedUnit, showProjectsFor } = this.state;
    return (
      (showProjectsFor.type === projSelectorConstants.DISTRICT && !selectedDistrict) ||
      (showProjectsFor.type === projSelectorConstants.UNIT && !selectedUnit)
    );
  };

  memoizedUnitOptions = memoizeOne((unitList) =>
    (unitList || []).map((u) => ({
      value: u.unitId,
      label: u.district + "-" + u.unitNumber + " (" + u.description + ")",
    }))
  );

  render() {
    const { showProjListSelectorModal, toggleProjListSelectorModal, unitList } =
      this.props;
    const { showProjectsFor, selectedDistrict, selectedUnit } = this.state;

    const closeBtn = (
      <button className="close" onClick={toggleProjListSelectorModal}>
        <FontAwesomeIcon icon="times" className="fa-fw text-white" />
      </button>
    );
    const unitOptions = this.memoizedUnitOptions(unitList);

    return (
      <Modal
        isOpen={showProjListSelectorModal}
        toggle={toggleProjListSelectorModal}
        className={this.props.className}
        fade={false}
        size={"md"}
      >
        <ModalHeader
          toggle={toggleProjListSelectorModal}
          className="bg-lightgray"
          close={closeBtn}
        >
          Re-populate Project List
        </ModalHeader>
        <ModalBody>
          <form id="projListSelectorForm" onSubmit={this.handleSubmit} autoComplete="off">
            <div className="card">
              <div className="card-body">
                <div className="form-row mb-2">
                  <div className="col-3 my-auto">
                    <div className="form-check-inline custom-control custom-radio mx-0">
                      <div className="radio">
                        <input
                          type="radio"
                          name={projSelectorConstants.DISTRICT}
                          id={projSelectorConstants.DISTRICT}
                          checked={
                            showProjectsFor.type === projSelectorConstants.DISTRICT
                          }
                          onChange={this.handleChange_radio}
                          className="custom-control-input"
                        />
                        <label
                          htmlFor={projSelectorConstants.DISTRICT}
                          className="custom-control-label"
                        >
                          District:
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="col-5">
                    <Select
                      name="forDistrict"
                      id="forDistrict"
                      className="align-middle"
                      value={selectedDistrict}
                      options={distOptions.without59}
                      onChange={this.handleChange_select(
                        projSelectorConstants.DISTRICT,
                        "selectedDistrict"
                      )}
                      isSearchable={false}
                      isDisabled={showProjectsFor.type !== projSelectorConstants.DISTRICT}
                      styles={{
                        // Fixes the overlapping problem of the component
                        menu: (provided) => ({ ...provided, zIndex: 9999 }),
                      }}
                    />
                  </div>
                </div>
                <div className="form-row mb-3">
                  <div className="col-3 my-auto">
                    <div className="form-check-inline custom-control custom-radio mx-0">
                      <div className="radio">
                        <input
                          type="radio"
                          name={projSelectorConstants.UNIT}
                          id={projSelectorConstants.UNIT}
                          checked={showProjectsFor.type === projSelectorConstants.UNIT}
                          onChange={this.handleChange_radio}
                          className="custom-control-input"
                        />
                        <label
                          htmlFor={projSelectorConstants.UNIT}
                          className="custom-control-label"
                        >
                          Unit:
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="col-8">
                    <Select
                      name="forUnit"
                      id="forUnit"
                      className="align-middle"
                      value={selectedUnit}
                      options={unitOptions}
                      onChange={this.handleChange_select(
                        projSelectorConstants.UNIT,
                        "selectedUnit"
                      )}
                      isSearchable
                      isDisabled={showProjectsFor.type !== projSelectorConstants.UNIT}
                    />
                  </div>
                </div>
                <div className="form-row mb-2">
                  <div className="col-6 my-auto">
                    <div className="form-check-inline custom-control custom-radio mx-0">
                      <div className="radio">
                        <input
                          type="radio"
                          name={projSelectorConstants.USER_ASSIGNED}
                          id="userAssigned"
                          checked={
                            showProjectsFor.type === projSelectorConstants.USER_ASSIGNED
                          }
                          onChange={this.handleChange_radio}
                          className="custom-control-input"
                        />
                        <label htmlFor="userAssigned" className="custom-control-label">
                          User-assigned
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </ModalBody>
        <ModalFooter className="bg-lightgray">
          <div className="form-row form-group mb-0">
            <input
              className="btn btn-primary"
              name="projListSelector"
              type="submit"
              form="projListSelectorForm"
              onClick={this.handleSubmit}
              value="Apply"
              disabled={this.formIsIncomplete()}
            />{" "}
            <button
              className="btn btn-secondary ml-2"
              color="secondary"
              onClick={toggleProjListSelectorModal}
            >
              Cancel
            </button>
          </div>
        </ModalFooter>
      </Modal>
    );
  }
}

const mapStateToProps = (state, ownProps) => ({
  unitList: state.users.openUnitLists[ownProps.tabId] || null,
  user: state.authentication.user,
});

export default connect(mapStateToProps)(ProjListSelectorModal);
